import { useFetchSelectedAgenciesRateDataContext } from 'modals/ShiftModal/src/views/SendToAgenciesView/SelectAgenciesView/contexts/FetchSelectedAgenciesRate.data.context';
import { useFetchSelectedMarketplaceCommunitiesRateDataContext } from 'modals/ShiftModal/src/views/SendToAgenciesView/SelectAgenciesView/contexts/FetchSelectedMarketplaceCommunitiesRate.data.context';
import { FC, useContext, useMemo } from 'react';
import { createLegacyDataContext } from 'utils/createDataContext';

interface IRateData {
  rate: {
    min: number;
    max: number;
  };
  cost: {
    min: number;
    max: number;
  };
}

export const FetchCombinedRatesDataContext = createLegacyDataContext<IRateData>({
  data: { rate: { min: 0, max: 0 }, cost: { min: 0, max: 0 } },
} as unknown as IRateData);

export const FetchCombinedRatesDataProvider: FC = ({ children }) => {
  const agencyRateContext = useFetchSelectedAgenciesRateDataContext();
  const marketplaceRateContext = useFetchSelectedMarketplaceCommunitiesRateDataContext();

  const combinedData = useMemo(() => {
    const agencyData = agencyRateContext.data;
    const marketplaceData = marketplaceRateContext.data;

    const rates = [];
    const costs = [];

    if (agencyData?.rate?.min) rates.push(agencyData.rate.min);
    if (agencyData?.rate?.max) rates.push(agencyData.rate.max);
    if (marketplaceData?.rate?.min) rates.push(marketplaceData.rate.min);
    if (marketplaceData?.rate?.max) rates.push(marketplaceData.rate.max);

    if (agencyData?.cost?.min) costs.push(agencyData.cost.min);
    if (agencyData?.cost?.max) costs.push(agencyData.cost.max);
    if (marketplaceData?.cost?.min) costs.push(marketplaceData.cost.min);
    if (marketplaceData?.cost?.max) costs.push(marketplaceData.cost.max);

    return {
      rate: {
        min: rates.length ? Math.min(...rates) : 0,
        max: rates.length ? Math.max(...rates) : 0,
      },
      cost: {
        min: costs.length ? Math.min(...costs) : 0,
        max: costs.length ? Math.max(...costs) : 0,
      },
    };
  }, [agencyRateContext.data, marketplaceRateContext.data]);

  const contextValue = useMemo(
    () => ({
      ...agencyRateContext,
      data: combinedData,
      status:
        agencyRateContext.status === 'LOADING' || marketplaceRateContext.status === 'LOADING'
          ? 'LOADING'
          : 'READY',
    }),
    [agencyRateContext, marketplaceRateContext, combinedData],
  );

  return (
    <FetchCombinedRatesDataContext.Provider value={contextValue}>
      {children}
    </FetchCombinedRatesDataContext.Provider>
  );
};

export const useFetchCombinedRatesDataContext = () => useContext(FetchCombinedRatesDataContext);
